import { put, call, takeLatest, all, select } from "redux-saga/effects";
import { GlobalSearchConstants } from "constants/Actions";
import API from "services";
import * as Actions from "actions";
import {
  ISearchHistoryResult,
  ISearchResponse,
} from "us.common/reducers/MainSearch/Interfaces";
import { ISearchRequestObject } from "us.common/components/MainSearch/Interfaces";
import {
  getData,
  formatArchiveSearchResult,
} from "us.common/functions/MainSearch";
import { getBoxUrl } from "components";

const { GlobalSearchActions } = Actions;

const globalSearchSaga = {
  all: {
    search: function* (action: any): any {
      const params: ISearchRequestObject = action.payload.data;
      let searchResponse: ISearchResponse = {
        invoice: [],
        case: [],
        creditor: [],
        debtor: [],
      };

      /**
       * Invoice search
       */
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.Invoice.search,
          params
        );

        let finalInvoiceResult = data;

        // Durable Function async path
        if (status === 202 && data?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            data.id
          );
          finalInvoiceResult = asyncData?.data;
        }

        yield put(GlobalSearchActions.invoice.success(getData(finalInvoiceResult)));
        searchResponse.invoice = finalInvoiceResult;
      } catch (error) {
        yield put(GlobalSearchActions.invoice.fail(error as Object));
      }

      /**
       * case
       */
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.CaseDetail.search,
          params
        );

        let finalCaseResult = data;

        // Durable Function async path
        if (status === 202 && data?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            data.id
          );
          finalCaseResult = asyncData?.data;
        }

        yield put(GlobalSearchActions.case.success(getData(finalCaseResult)));
        searchResponse.case = finalCaseResult;
      } catch (error) {
        yield put(GlobalSearchActions.case.fail(error as Object));
      }

      /**
       * Debtor search
       */
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.DebtorDetail.search,
          params
        );

        let finalDebtorResult = data;

        // Durable Function async path
        if (status === 202 && data?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            data.id
          );
          finalDebtorResult = asyncData?.data;
        }

        yield put(GlobalSearchActions.debtor.success(getData(finalDebtorResult)));
        searchResponse.debtor = finalDebtorResult;
      } catch (error) {
        yield put(GlobalSearchActions.debtor.fail(error as Object));
      }

      /**
       * Creditor search
       */
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.CreditorDetail.search,
          params
        );

        let finalCreditorResult = data;

        // Durable Function async path
        if (status === 202 && data?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            data.id
          );
          finalCreditorResult = asyncData?.data;
        }

        yield put(GlobalSearchActions.creditor.success(getData(finalCreditorResult)));
        searchResponse.creditor = finalCreditorResult;
      } catch (error) {
        yield put(GlobalSearchActions.creditor.fail(error as Object));
      }

      const navigationDetail = (state: any) => state.mainSearch.backNavigation;
      const backNavigation: { isBack: boolean } = yield select(
        navigationDetail
      );

      const companyConfig = yield select(
        (state: any) => state.globalSearch.companyConfig
      );

      if (
        getData(searchResponse.invoice).length === 1 &&
        getData(searchResponse.case).length === 0 &&
        getData(searchResponse.creditor).length === 0 &&
        getData(searchResponse.debtor).length === 0 &&
        !backNavigation.isBack
      ) {
        const routeUrl = getBoxUrl(companyConfig?.data, searchResponse?.invoice[0]?.companyName);
        if (routeUrl) {
          window.open(routeUrl + `/case/${searchResponse.invoice[0].exCaseNo}`);
        }
      } else if (
        getData(searchResponse.invoice).length === 0 &&
        getData(searchResponse.case).length === 1 &&
        getData(searchResponse.creditor).length === 0 &&
        getData(searchResponse.debtor).length === 0 &&
        !backNavigation.isBack
      ) {
        const routeUrl = getBoxUrl(companyConfig?.data, searchResponse?.case[0]?.companyName);
        if (routeUrl) {
          window.open(routeUrl + `/case/${searchResponse.case[0]?.exCaseNo}`);
        }
      } else if (
        getData(searchResponse.invoice).length === 0 &&
        getData(searchResponse.case).length === 0 &&
        getData(searchResponse.creditor).length === 0 &&
        getData(searchResponse.debtor).length === 1 &&
        getData(searchResponse.debtor[0].arDetails).length === 1 &&
        !backNavigation.isBack
      ) {
        const routeUrl = getBoxUrl(companyConfig?.data, searchResponse.debtor[0]?.companyName);
        if (routeUrl) {
          window.open(routeUrl + `/ar/${searchResponse.debtor[0]?.arDetails[0]?.arNo}`);
        }
      } else if (
        getData(searchResponse.invoice).length === 0 &&
        getData(searchResponse.case).length === 0 &&
        getData(searchResponse.debtor).length === 0 &&
        getData(searchResponse.creditor).length === 1 &&
        !backNavigation.isBack
      ) {
        const routeUrl = getBoxUrl(companyConfig?.data, searchResponse.creditor[0]?.companyName);
        if (routeUrl) {
          window.open(routeUrl + `/creditor/${searchResponse.creditor[0]?.creditorNumber}`);
        }
      }
    },
  },
  invoiceRef: {
    search: function* (action: any): any {
      const params: ISearchRequestObject = action.payload.data;
      try {
        const [{invoiceData, invoiceStatus}, {caseData, caseStatus}] = yield all([
          call(API.GlobalSearchService.Invoice.search, params),
          call(API.GlobalSearchService.CaseDetail.search, params),
        ]);

        let finalInvoiceResult = invoiceData;
        let finalCaseResult = caseData;

        // Durable Function async path
        if (invoiceStatus === 202 && invoiceData?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            invoiceData.id
          );
          finalInvoiceResult = asyncData?.data;
        }

        if (caseStatus === 202 && caseData?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            caseData.id
          );
          finalCaseResult = asyncData?.data;
        }

        yield put(
          GlobalSearchActions.invoiceRef.success({
            invoiceses: getData(finalInvoiceResult),
            cases: getData(finalCaseResult),
          })
        );
        const navigationDetail = (state: any) =>
          state.mainSearch.backNavigation;
        const backNavigation: { isBack: boolean } = yield select(
          navigationDetail
        );
        const companyConfig = yield select(
          (state: any) => state.globalSearch.companyConfig
        );

        if (
          getData(finalInvoiceResult).length === 1 &&
          getData(finalCaseResult).length === 0 &&
          !backNavigation.isBack
        ) {
          const routeUrl = getBoxUrl(companyConfig?.data, finalInvoiceResult[0]?.companyName);
          if (routeUrl) {
            window.open(routeUrl + `/case/${finalInvoiceResult[0].exCaseNo}`);
          }
        } else if (
          getData(finalInvoiceResult).length === 0 &&
          getData(finalCaseResult).length === 1 &&
          !backNavigation.isBack
        ) {
          const routeUrl = getBoxUrl(companyConfig?.data, finalCaseResult[0]?.companyName);
          if (routeUrl) {
            window.open(routeUrl + `/case/${finalCaseResult[0].exCaseNo}`);
          }
        }
      } catch (error) {
        yield put(GlobalSearchActions.invoiceRef.fail(error as Object));
      }
    },
  },
  invoice: {
    search: function* (action: any) {
      const params: ISearchRequestObject = action.payload.data;
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.Invoice.search,
          params
        );
        let finalResult = data;

        // Durable Function async path
        if (status === 202 && data?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            data.id
          );
          finalResult = asyncData?.data;
        }

        yield put(GlobalSearchActions.invoice.success(getData(finalResult)));
        const navigationDetail = (state: any) =>
          state.mainSearch.backNavigation;
        const backNavigation: { isBack: boolean } = yield select(
          navigationDetail
        );
        const companyConfig: { data: Array<any>; isLoading: boolean; } = yield select(
          (state: any) => state.globalSearch.companyConfig
        );
        if (
          getData(finalResult).length === 1 &&
          action.payload.data.Hit === 1 &&
          !backNavigation.isBack
        ) {
          const routeUrl = getBoxUrl(companyConfig?.data, getData(finalResult)[0]?.companyName);
          if (routeUrl) {
            window.open(routeUrl + `/case/${getData(finalResult)[0].exCaseNo}`);
          }
        }
      } catch (error) {
        yield put(GlobalSearchActions.invoice.fail(error as Object));
      }
    },
  },
  case: {
    search: function* (action: any): any {
      const params: ISearchRequestObject = action.payload.data;
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.CaseDetail.search,
          params
        );

        let finalResult = data;

        // Durable Function async path
        if (status === 202 && data?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            data.id
          );
          finalResult = asyncData?.data;
        }

        yield put(GlobalSearchActions.case.success(getData(finalResult)));
        const navigationDetail = (state: any) =>
          state.mainSearch.backNavigation;
        const backNavigation: { isBack: boolean } = yield select(
          navigationDetail
        );

        const companyConfig: { data: Array<any>; isLoading: boolean; } = yield select(
          (state: any) => state.globalSearch.companyConfig
        );
        if (
          getData(finalResult).length === 1 &&
          action.payload.data.Hit === 1 &&
          !backNavigation.isBack
        ) {
          const routeUrl = getBoxUrl(companyConfig?.data, getData(finalResult)[0]?.companyName);
          if (routeUrl) {
            window.open(routeUrl + `/case/${getData(finalResult)[0].exCaseNo}`);
          }
        }
      } catch (error) {
        yield put(GlobalSearchActions.case.fail(error as Object));
      }
    },
  },
  debtor: {
    search: function* (action: any) {
      const params: ISearchRequestObject = action.payload.data;
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.DebtorDetail.search,
          params
        );

        let finalResult = data;

        // Durable Function async path
        if (status === 202 && data?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            data.id
          );
          finalResult = asyncData?.data;
        }

        yield put(GlobalSearchActions.debtor.success(getData(finalResult)));
        const navigationDetail = (state: any) =>
          state.mainSearch.backNavigation;
        const backNavigation: { isBack: boolean } = yield select(
          navigationDetail
        );

        const companyConfig: { data: Array<any>; isLoading: boolean; } = yield select(
          (state: any) => state.globalSearch.companyConfig
        );

        if (
          action.payload.data.Hit === 1 &&
          finalResult &&
          getData(finalResult).length === 1 &&
          getData(finalResult[0].arDetails).length === 1 &&
          !backNavigation.isBack
        ) {
          const routeUrl = getBoxUrl(companyConfig?.data, finalResult[0]?.companyName);
          if (routeUrl) {
            window.open(routeUrl + `/ar/${finalResult[0].arDetails[0].arNo}`);
          }
        }
      } catch (error) {
        yield put(GlobalSearchActions.debtor.fail(error as Object));
      }
    },
  },
  creditor: {
    search: function* (action: any) {
      const params: ISearchRequestObject = action.payload.data;
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.CreditorDetail.search,
          params
        );

        let finalResult = data;

        // Durable Function async path
        if (status === 202 && data?.id) {
          const {data: asyncData, status: asyncStatus} = yield call(
            API.GlobalSearchService.result.get,
            data.id
          );
          finalResult = asyncData?.data;
        }

        yield put(GlobalSearchActions.creditor.success(getData(finalResult)));
        const navigationDetail = (state: any) =>
          state.mainSearch.backNavigation;
        const backNavigation: { isBack: boolean } = yield select(
          navigationDetail
        );
        const companyConfig: { data: Array<any>; isLoading: boolean; } = yield select(
          (state: any) => state.globalSearch.companyConfig
        );

        if (
          action.payload.data.Hit === 1 &&
          finalResult &&
          getData(finalResult).length === 1 &&
          !backNavigation.isBack
        ) {
          const routeUrl = getBoxUrl(companyConfig?.data, finalResult[0]?.companyName);
          if (routeUrl) {
            window.open(routeUrl + `/creditor/${finalResult[0].creditorNumber}`);
          }
        }
      } catch (error) {
        yield put(GlobalSearchActions.creditor.fail(error as Object));
      }
    },
  },
  history: {
    get: function* (action: any) {
      try {
        const data: ISearchHistoryResult[] | undefined = yield call(
          API.GlobalSearchService.history.get
        );
        yield put(GlobalSearchActions.history.success(getData(data)));
      } catch (error) {
        yield put(GlobalSearchActions.history.fail(error as Object));
      }
    },
    save: function* (action: any) {
      const params = action.payload.data;
      try {
        const data: ISearchHistoryResult[] | undefined = yield call(
          API.GlobalSearchService.history.save,
          params
        );
        yield put(GlobalSearchActions.history.success(getData(data)));
      } catch (error) {
        yield put(GlobalSearchActions.history.fail(error as Object));
      }
    },
  },
  archive: {
    search: function* (action: any) {
      try {
        const params = action.payload.data;
        const { data, status } = yield call(
          API.GlobalSearchService.archivedCase.get,
          params
        );
        if (Array.isArray(data) && status == 200) {
          yield put(
            GlobalSearchActions.archiveCases.success(
              formatArchiveSearchResult(data)
            )
          );
        } else {
          yield put(GlobalSearchActions.archiveCases.fail([]));
        }
      } catch (error) {
        yield put(GlobalSearchActions.archiveCases.fail(error as Object));
      }
    },
    getArchivedCaseDetails: function* (action: any) {
      try {
        const params = action.payload.data;
        const { data, status } = yield call(
          API.GlobalSearchService.archivedCase.getDetails,
          params
        );
        if (data && status == 200) {
          yield put(GlobalSearchActions.archiveCaseDetails.success(data));
        } else {
          yield put(GlobalSearchActions.archiveCaseDetails.fail({}));
        }
      } catch (error) {
        yield put(GlobalSearchActions.archiveCaseDetails.fail(error as Object));
      }
    },
  },
  companyConfig: {
    get: function* (action: any) {
      try {
        const { data, status } = yield call(
          API.GlobalSearchService.company.get,
          {}
        );
        if (Array.isArray(data) && status == 200) {
          yield put(GlobalSearchActions.companyConfig.success(data));
        } else {
          yield put(GlobalSearchActions.companyConfig.fail([]));
        }
      } catch (error) {
        yield put(GlobalSearchActions.companyConfig.fail(error as Object));
      }
    },
  },
};

export default [
  takeLatest(
    GlobalSearchConstants.GET_ALL_GLOBAL_SEARCH_DATA,
    globalSearchSaga.all.search
  ),
  takeLatest(
    GlobalSearchConstants.GET_INVOICE_REF_GLOBAL_SEARCH_DATA,
    globalSearchSaga.invoiceRef.search
  ),
  takeLatest(
    GlobalSearchConstants.GET_INVOICE_GLOBAL_SEARCH_DATA,
    globalSearchSaga.invoice.search
  ),
  takeLatest(
    GlobalSearchConstants.GET_CASE_GLOBAL_SEARCH_DATA,
    globalSearchSaga.case.search
  ),
  takeLatest(
    GlobalSearchConstants.GET_DEBTOR_GLOBAL_SEARCH_DATA,
    globalSearchSaga.debtor.search
  ),
  takeLatest(
    GlobalSearchConstants.GET_CREDITOR_GLOBAL_SEARCH_DATA,
    globalSearchSaga.creditor.search
  ),
  takeLatest(
    GlobalSearchConstants.GET_GLOBAL_SEARCH_HISTORY,
    globalSearchSaga.history.get
  ),
  takeLatest(
    GlobalSearchConstants.SAVE_GLOBAL_SEARCH_HISTORY,
    globalSearchSaga.history.save
  ),
  takeLatest(
    GlobalSearchConstants.GET_ARCHIVE_GLOBAL_SEARCH_DATA,
    globalSearchSaga.archive.search
  ),
  takeLatest(
    GlobalSearchConstants.GET_ARCHIVE_GLOBAL_CASE_DETAILS,
    globalSearchSaga.archive.getArchivedCaseDetails
  ),
  takeLatest(
    GlobalSearchConstants.GET_COMPANY_CONFIG,
    globalSearchSaga.companyConfig.get
  ),
];
